import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import {
  createDeckCategory,
  createLanguageVersion,
  createPresentation,
  deleteDeckCategory,
  deletePresentation,
  duplicatePresentation,
  listDeckCategories,
  listPresentations,
  listTemplates,
  patchDeckCategory,
  patchPresentation,
} from './api';
import { catalogCategoryKind, catalogPageFormat, preferredCreateTemplateId, templatesForCatalog } from './catalog';
import { CONTROL, PRES, TYPE } from './chrome';
import LanguageVersionDialog from './LanguageVersionDialog';
import { catalogSiblingIds, familyMembersOf, groupCatalogItems } from './languageVersionHelpers';
import { COPY, languageLabel, marketLabel, pageFormatLabel, statusLabel } from './presentationCopy';
import { PresentationCatalog, PresentationDeckCategory, PresentationLanguage, PresentationMarket, PresentationRecord, PresentationStatus, PresentationTemplate } from './types';
import CoverCard from './CoverCard';
import DeckCategoryNav from './DeckCategoryNav';
import { childDeckCategories, rootDeckCategories, UNCATEGORIZED_DECK_FILTER } from './deckCategoryTree';
import { EmptyState, Pill, SkeletonCard } from './ui';
import { pillToneForStatus } from './ui/pillTone';
import { focusVisibleSx } from './ui/focus';
import SettingsGearButton from './SettingsGearButton';
import GenerateDialog from './generate/GenerateDialog';
import { getGenerationCapabilities } from './generate/api';
import { isPresentationsAiGenerateEnabled } from './presentationsFlags';
import { usePresentationSettings } from './PresentationSettingsContext';
import {
  settingsLanguageLabel,
  settingsMarketLabel,
  visiblePresentationLanguages,
  visiblePresentationMarkets,
} from './presentationSettings';

interface CatalogHomeProps {
  catalog: PresentationCatalog;
  canCreate: boolean;
  canEdit: boolean;
  canManageCategories: boolean;
  canManageTemplates: boolean;
  onClose: () => void;
  onOpen: (id: string) => void;
  onOpenSalesSheets?: () => void;
  onOpenTemplates?: () => void;
  onOpenLibrary?: () => void;
  onOpenSettings?: () => void;
}

const CatalogHome: React.FC<CatalogHomeProps> = ({
  catalog,
  canCreate,
  canEdit,
  canManageCategories,
  canManageTemplates,
  onClose,
  onOpen,
  onOpenSalesSheets,
  onOpenTemplates,
  onOpenLibrary,
  onOpenSettings,
}) => {
  const { settings } = usePresentationSettings();
  const visibleLanguages = visiblePresentationLanguages(settings);
  const visibleMarkets = visiblePresentationMarkets(settings);
  const isSalesSheets = catalog === 'salesSheets';
  const pageFormat = catalogPageFormat(catalog);
  const categoryKind = catalogCategoryKind(catalog);
  const [items, setItems] = useState<PresentationRecord[]>([]);
  const [categories, setCategories] = useState<PresentationDeckCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [market, setMarket] = useState<PresentationMarket>('SE');
  const [language, setLanguage] = useState<PresentationLanguage>('sv');
  const [createCategoryId, setCreateCategoryId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateEnabled, setGenerateEnabled] = useState(false);
  const [languageSource, setLanguageSource] = useState<PresentationRecord | null>(null);
  const [languageBusy, setLanguageBusy] = useState(false);
  const [languageError, setLanguageError] = useState<string | null>(null);
  const [createTemplates, setCreateTemplates] = useState<PresentationTemplate[]>([]);
  const [createTemplateId, setCreateTemplateId] = useState('');
  const [statusFilter, setStatusFilter] = useState<PresentationStatus | 'all'>('all');
  const [deckCategoryId, setDeckCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const reload = async () => {
    setLoading(true);
    try {
      const [nextItems, nextCategories] = await Promise.all([
        listPresentations({
          q: debouncedSearch,
          deckCategoryId: deckCategoryId || undefined,
          status: statusFilter,
          pageFormat,
        }),
        listDeckCategories(categoryKind),
      ]);
      setItems(nextItems);
      setCategories(nextCategories);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.loadFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void reload(); }, [debouncedSearch, deckCategoryId, statusFilter, pageFormat, categoryKind]);

  useEffect(() => {
    if (isSalesSheets || !canCreate) return;
    void getGenerationCapabilities().then((capabilities) => {
      setGenerateEnabled(Boolean(
        isPresentationsAiGenerateEnabled(capabilities?.enabled) && capabilities?.configured,
      ));
    });
  }, [isSalesSheets, canCreate]);

  useEffect(() => {
    if (visibleLanguages.length && !visibleLanguages.includes(language)) setLanguage(visibleLanguages[0]);
    if (visibleMarkets.length && !visibleMarkets.includes(market)) setMarket(visibleMarkets[0]);
  }, [visibleLanguages, visibleMarkets, language, market]);

  const openCreate = async () => {
    setName('');
    setCreateCategoryId(deckCategoryId === UNCATEGORIZED_DECK_FILTER ? '' : deckCategoryId);
    try {
      const templates = templatesForCatalog(await listTemplates(), catalog);
      setCreateTemplates(templates);
      setCreateTemplateId(preferredCreateTemplateId(templates, catalog));
    } catch {
      setCreateTemplates([]);
      setCreateTemplateId('');
    }
    setCreateOpen(true);
  };

  const openGenerate = async () => {
    setCreateCategoryId(deckCategoryId === UNCATEGORIZED_DECK_FILTER ? '' : deckCategoryId);
    try {
      const templates = templatesForCatalog(await listTemplates(), catalog);
      setCreateTemplates(templates);
      setCreateTemplateId(preferredCreateTemplateId(templates, catalog));
    } catch {
      setCreateTemplates([]);
    }
    setGenerateOpen(true);
  };

  const canSubmit = Boolean(name.trim() && (!isSalesSheets || createTemplateId));
  const categoryOptions = rootDeckCategories(categories).flatMap((root) => [
    { id: root._id, label: root.name },
    ...childDeckCategories(categories, root._id).map((child) => ({ id: child._id, label: `${root.name} / ${child.name}` })),
  ]);
  const hasFilters = Boolean(debouncedSearch || deckCategoryId || statusFilter !== 'all');
  const groups = groupCatalogItems(items);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: PRES.surfaceSunken, fontFamily: TYPE.ui }}>
      <Box sx={{
        height: 56,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        bgcolor: PRES.surface,
        borderBottom: `1px solid ${PRES.borderSubtle}`,
        flexShrink: 0,
      }}>
        {isSalesSheets ? (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onClose}
            sx={{
              textTransform: 'none',
              color: PRES.interactive,
              fontWeight: 650,
              ...focusVisibleSx,
            }}
          >
            {COPY.allPresentations}
          </Button>
        ) : (
          <IconButton onClick={onClose} size="small" sx={{ color: PRES.textPrimary, ...focusVisibleSx }} title={COPY.backToAthena}>
            <ArrowBackIcon />
          </IconButton>
        )}
        {isSalesSheets && (
          <Box sx={{ width: '1px', alignSelf: 'stretch', my: 1.5, bgcolor: PRES.borderStrong, flexShrink: 0 }} />
        )}
        <Typography sx={{ color: PRES.textPrimary, fontFamily: TYPE.ui, ...TYPE.title }}>
          {isSalesSheets ? COPY.salesSheets : COPY.moduleTitle}
        </Typography>
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={isSalesSheets ? COPY.searchSalesSheets : COPY.searchPresentations}
          sx={{ ml: 2, flex: 1, maxWidth: 420, '& .MuiInputBase-root': { fontFamily: TYPE.ui, ...TYPE.body } }}
        />
        {!isSalesSheets && onOpenSalesSheets && (
          <Button onClick={onOpenSalesSheets} sx={{ textTransform: 'none', color: PRES.textPrimary }}>
            {COPY.salesSheets}
          </Button>
        )}
        {canManageTemplates && onOpenTemplates && (
          <Button onClick={onOpenTemplates} sx={{ textTransform: 'none', color: PRES.textPrimary }}>
            {COPY.templates}
          </Button>
        )}
        {canManageTemplates && onOpenLibrary && (
          <Button onClick={onOpenLibrary} sx={{ textTransform: 'none', color: PRES.textPrimary }}>
            {COPY.slideLibrary}
          </Button>
        )}
        {canCreate && !isSalesSheets && generateEnabled && (
          <Button
            variant="outlined"
            onClick={() => { void openGenerate(); }}
            sx={{ textTransform: 'none', color: PRES.interactive, borderColor: PRES.borderStrong }}
          >
            {COPY.createWithAi}
          </Button>
        )}
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { void openCreate(); }}
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: PRES.interactiveHover } }}
          >
            {isSalesSheets ? COPY.newSalesSheet : COPY.newPresentation}
          </Button>
        )}
        {onOpenSettings ? <SettingsGearButton onClick={onOpenSettings} /> : null}
      </Box>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <DeckCategoryNav
          categories={categories}
          selectedId={deckCategoryId}
          canManage={canManageCategories}
          deleteConfirm={isSalesSheets ? COPY.confirmDeleteCategorySalesSheet : COPY.confirmDeleteCategory}
          onSelect={setDeckCategoryId}
          onCreate={async (categoryName, parentCategory) => {
            try {
              await createDeckCategory({
                name: categoryName,
                parentCategory: parentCategory || null,
                kind: categoryKind,
              });
              await reload();
            } catch (err: any) {
              setError(err?.response?.data?.message || COPY.loadFailed);
            }
          }}
          onRename={async (id, categoryName) => {
            try {
              await patchDeckCategory(id, { name: categoryName });
              await reload();
            } catch (err: any) {
              setError(err?.response?.data?.message || COPY.loadFailed);
            }
          }}
          onDelete={async (id) => {
            try {
              await deleteDeckCategory(id);
              if (deckCategoryId === id) setDeckCategoryId('');
              await reload();
            } catch (err: any) {
              setError(err?.response?.data?.message || COPY.categoryHasChildren);
            }
          }}
        />
        <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {(['all', 'draft', 'review', 'ready'] as const).map((status) => (
              <Box
                key={status}
                component="button"
                type="button"
                onClick={() => setStatusFilter(status)}
                sx={{ border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', opacity: statusFilter === status ? 1 : 0.55 }}
              >
                <Pill tone={status === 'all' ? 'neutral' : pillToneForStatus(status)}>
                  {status === 'all' ? COPY.filterAll : statusLabel(status)}
                </Pill>
              </Box>
            ))}
          </Box>
          {loading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2 }}>
              {Array.from({ length: 8 }).map((_, index) => <SkeletonCard key={index} />)}
            </Box>
          ) : items.length ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2 }}>
              {groups.map((group) => (
                <CoverCard
                  key={group.key}
                  item={group.item}
                  displayName={group.displayName}
                  members={group.members}
                  openId={group.openId}
                  originReviewPending={group.originReviewPending}
                  libraryUpdatePending={group.libraryUpdatePending}
                  canEdit={canEdit}
                  canCreate={canCreate}
                  categories={categories}
                  onOpen={onOpen}
                  onRename={async (nextName) => {
                    try {
                      await patchPresentation(group.actionId, { name: nextName });
                      await reload();
                    } catch (err: any) {
                      setError(err?.response?.data?.message || COPY.saveError);
                    }
                  }}
                  onDuplicate={async () => {
                    try {
                      await duplicatePresentation(group.actionId);
                      await reload();
                    } catch (err: any) {
                      setError(err?.response?.data?.message || COPY.duplicatePresentationFailed);
                    }
                  }}
                  onCreateLanguageVersion={canCreate ? () => {
                    setLanguageError(null);
                    setLanguageSource({
                      ...group.item,
                      _id: group.actionId,
                      name: group.displayName,
                      familyMembers: group.members,
                    });
                  } : undefined}
                  onMoveToCategory={async (nextCategoryId) => {
                    try {
                      const ids = catalogSiblingIds(group.members, group.actionId);
                      for (const id of ids) {
                        await patchPresentation(id, { deckCategoryId: nextCategoryId });
                      }
                      await reload();
                    } catch (err: any) {
                      setError(err?.response?.data?.message || COPY.saveError);
                      await reload();
                    }
                  }}
                  onDelete={async () => {
                    try {
                      await deletePresentation(group.actionId);
                      await reload();
                    } catch (err: any) {
                      setError(err?.response?.data?.message || COPY.deleteFailed);
                    }
                  }}
                />
              ))}
            </Box>
          ) : (
            <EmptyState
              title={hasFilters
                ? (isSalesSheets ? COPY.noneInFilterSalesSheets : COPY.noneInFilter)
                : (isSalesSheets ? COPY.noneYetSalesSheets : COPY.noneYet)}
              body={hasFilters
                ? COPY.noneInFilterBody
                : (isSalesSheets ? COPY.noneYetSalesSheetsBody : COPY.noneYetBody)}
              action={!hasFilters && canCreate ? (
                <Button
                  variant="contained"
                  onClick={() => { void openCreate(); }}
                  sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
                >
                  {isSalesSheets ? COPY.newSalesSheet : COPY.newPresentation}
                </Button>
              ) : undefined}
            />
          )}
        </Box>
      </Box>
      <LanguageVersionDialog
        open={Boolean(languageSource)}
        members={languageSource ? familyMembersOf(languageSource) : []}
        busy={languageBusy}
        error={languageError}
        onClose={() => {
          if (languageBusy) return;
          setLanguageSource(null);
          setLanguageError(null);
        }}
        onCreate={async (input) => {
          if (!languageSource) return;
          setLanguageBusy(true);
          setLanguageError(null);
          try {
            await createLanguageVersion(languageSource._id, input);
            setLanguageSource(null);
            await reload();
          } catch (err: any) {
            setLanguageError(err?.response?.data?.message || COPY.languageVersionFailed);
          } finally {
            setLanguageBusy(false);
          }
        }}
      />
      <GenerateDialog
        open={generateOpen}
        templates={createTemplates}
        language={language}
        market={market}
        deckCategoryId={createCategoryId || null}
        onClose={() => setGenerateOpen(false)}
        onCreated={(id) => {
          setGenerateOpen(false);
          onOpen(id);
        }}
      />
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{isSalesSheets ? COPY.createSalesSheetTitle : COPY.createTitle}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          <TextField
            autoFocus
            label={COPY.name}
            placeholder={isSalesSheets ? COPY.namePlaceholderSalesSheet : COPY.namePlaceholder}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Select size="small" value={market} onChange={(event) => setMarket(event.target.value as PresentationMarket)}>
            {visibleMarkets.map((value) => (
              <MenuItem key={value} value={value}>{marketLabel(value, settingsMarketLabel(settings, value))}</MenuItem>
            ))}
          </Select>
          <Select size="small" value={language} onChange={(event) => setLanguage(event.target.value as PresentationLanguage)}>
            {visibleLanguages.map((value) => (
              <MenuItem key={value} value={value}>{languageLabel(value, settingsLanguageLabel(settings, value))}</MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            displayEmpty
            value={createCategoryId}
            onChange={(event) => setCreateCategoryId(event.target.value)}
          >
            <MenuItem value="">{COPY.noDeckCategory}</MenuItem>
            {categoryOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
            ))}
          </Select>
          {createTemplates.length > 0 ? (
            <Select
              size="small"
              value={createTemplateId}
              onChange={(event) => setCreateTemplateId(event.target.value)}
            >
              {createTemplates.map((template) => (
                <MenuItem key={template._id} value={template._id}>{COPY.templateLabel}: {template.name} · {pageFormatLabel(template.pageFormat)}</MenuItem>
              ))}
            </Select>
          ) : isSalesSheets ? (
            <Alert severity="info">{COPY.noSalesSheetTemplates}</Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            disabled={!canSubmit}
            variant="contained"
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            onClick={async () => {
              try {
                const created = await createPresentation({
                  name: name.trim(),
                  market,
                  language,
                  deckCategoryId: createCategoryId || null,
                  templateId: createTemplateId || undefined,
                });
                setCreateOpen(false);
                onOpen(created._id);
              } catch (err: any) {
                setError(err?.response?.data?.message || COPY.createFailed);
              }
            }}
          >
            {COPY.createAction}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CatalogHome;
